import { Spinner } from '@/components/ui/spinner'
import { Toast, ToastDescription, useToast } from '@/components/ui/toast'
import useDefaultToast from '@/components/useDefaultToast'
import { saveFileFromRemoteUrl } from '@/lib/utils/fsSaver'
import { useCallback, useRef } from 'react'

const PREPARING_FILE = '正在准备文件…'

/** Downloads a file and hands it to the share sheet, saved under `fileName` when one is given. */
export default function useExportFile() {
  const toast = useToast()
  const defaultToast = useDefaultToast()
  const isExporting = useRef(false)

  // The download runs before the share sheet appears, seconds with nothing on screen; a reader who taps again
  // meanwhile would start a second one.
  return useCallback(
    async (url: string, fileName: string) => {
      if (isExporting.current) return
      isExporting.current = true
      const loadingId = toast.show({
        placement: 'top',
        duration: null,
        render: ({ id }) => (
          <Toast nativeID={`toast-${id}`} action="muted" variant="solid" className="flex-row items-center gap-2">
            <Spinner className="text-typography-0" />
            <ToastDescription>{PREPARING_FILE}</ToastDescription>
          </Toast>
        ),
      })
      const closeLoading = () => toast.close(loadingId)
      try {
        // The same file downloaded again replaces the copy left in the cache instead of failing
        await saveFileFromRemoteUrl(url, {
          fileName,
          download: { idempotent: true },
          onDownloaded: closeLoading,
        })
      } catch (error) {
        console.error(error)
        closeLoading()
        defaultToast.show('文件下载失败，请稍后重试')
      } finally {
        isExporting.current = false
      }
    },
    [toast, defaultToast]
  )
}
